/**
 * ADR-0240 Slice 2a — "Referral link & signups" page.
 *
 * The agency-side read + edit of the per-agency referral slug. Shows:
 *
 *   - the public `go_url` + copy button (first-run form when the slug
 *     is unset — "Choose a slug" + an input)
 *   - slug editor (character counter 3–32; "Lowercase letters,
 *     numbers, dashes, underscores")
 *   - three counters: clicks (7 d), clicks (30 d), signups
 *   - attributed-accounts list
 *
 * Home-session only (home account's agency feature ON, non-acting).
 * The surrounding routes already deny acting sessions; this view
 * renders honest copy for the off/acting states.
 */

import { Check, Copy } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import {
  getAgencyReferral,
  putAgencyReferral,
  type AgencyReferralStateWire,
  type PutAgencyReferralResult,
} from '../../api/agency-referral.js';
import { useC } from '../../lib/theme.js';
import { Button, Card, NumLabel } from '../../components/atoms/index.js';
import { useIsEmbedded } from '../agency-portal/portal-shell-context.js';
import { mapReason } from '../../lib/humanize-agency-error.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; state: AgencyReferralStateWire }
  | { kind: 'error'; message: string };

/**
 * Map the typed PUT result → the human copy the input shows. Server
 * errors are rendered as plain sentences, never as error codes.
 */
function humanizeSaveResult(r: Exclude<PutAgencyReferralResult, { kind: 'ok' }>): string {
  switch (r.kind) {
    case 'invalid_shape':
      return 'Use 3–32 characters: lowercase letters, numbers, dashes or underscores. Must start and end with a letter or number.';
    case 'slug_reserved':
      return 'That slug is reserved. Try another.';
    case 'slug_taken':
      return 'That slug is already in use by another agency.';
    case 'forbidden':
      return 'You do not have permission to edit this.';
    case 'error':
      return r.message;
  }
}

export function AgencyReferral(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const { features, acting } = useAgency();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const r = await getAgencyReferral(http);
    if (r.kind === 'ok') {
      setState({ kind: 'ok', state: r.state });
      setDraft(r.state.slug ?? '');
    } else if (r.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Agency access is not available on this account.' });
    } else {
      // v3 (2026-10-10) — never render a raw code; map through the
      // shared humanize helper first.
      setState({ kind: 'error', message: mapReason(r.message) ?? 'We could not load the Referral page. Refresh to try again.' });
    }
  }, [http]);

  useEffect(() => {
    if (features === null) return;
    if (features.agency !== true) return;
    if (acting !== null) return;
    void refresh();
  }, [features, acting, refresh]);

  const onSave = async (): Promise<void> => {
    if (http === null || saving) return;
    setSaving(true);
    setSaveError(null);
    const r = await putAgencyReferral(http, draft.trim().toLowerCase());
    if (r.kind === 'ok') {
      setSaving(false);
      await refresh();
      return;
    }
    setSaving(false);
    setSaveError(humanizeSaveResult(r));
  };

  const onCopy = async (): Promise<void> => {
    if (state.kind !== 'ok' || state.state.go_url === null) return;
    try {
      await navigator.clipboard.writeText(state.state.go_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — fail silently; the link is already on
      // screen for manual copy.
    }
  };

  // Guardrails ------------------------------------------------------
  if (features === null) {
    return (
      <ReferralShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </ReferralShell>
    );
  }
  if (acting !== null) {
    return (
      <ReferralShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Return to your home account to manage your referral link.
          </div>
        </Card>
      </ReferralShell>
    );
  }
  if (features.agency !== true) {
    return (
      <ReferralShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>
            Referral links are an agency add-on.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Contact Sill to enable the agency surface on this account.
          </div>
        </Card>
      </ReferralShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <ReferralShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </ReferralShell>
    );
  }
  if (state.kind === 'error') {
    return (
      <ReferralShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{state.message}</div>
        </Card>
      </ReferralShell>
    );
  }

  const s = state.state;
  const canSave = draft.length >= 3 && draft.length <= 32 && !saving;

  const conversion =
    s.clicks_total_30d >= 20
      ? `${((s.signups_total / Math.max(1, s.clicks_total_30d)) * 100).toFixed(1)}%`
      : '—';

  return (
    <ReferralShell>
      {/* Analytics first (Agency Portal v2 §4.5) */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        style={{ marginBottom: 16 }}
        data-testid="agency-referral-metrics"
      >
        <Counter label="Clicks · 7 days" value={s.clicks_total_7d.toLocaleString()} />
        <Counter label="Clicks · 30 days" value={s.clicks_total_30d.toLocaleString()} />
        <Counter label="Signups · all time" value={s.signups_total.toLocaleString()} />
        <Counter label="Click → signup" value={conversion} />
      </div>

      {/* Link + copy */}
      <Card className="p-5 sm:p-6" style={{ marginBottom: 16 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Your referral link
        </div>
        {s.go_url !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <code
              data-testid="agency-referral-url"
              style={{
                fontFamily: monoFont,
                fontSize: 13,
                color: C.text,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                padding: '7px 10px',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {s.go_url}
            </code>
            <button
              type="button"
              onClick={() => {
                void onCopy();
              }}
              aria-label="Copy referral link"
              className="flex items-center gap-2 px-3"
              style={{
                height: 32,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                background: C.surface,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
              }}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 16 }}>
            Choose a slug below to activate your referral link.
          </div>
        )}

        <label
          htmlFor="agency-referral-slug-input"
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Link name
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <input
            id="agency-referral-slug-input"
            type="text"
            maxLength={32}
            autoComplete="off"
            spellCheck={false}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setSaveError(null);
            }}
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              padding: '7px 10px',
              flex: 1,
            }}
          />
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              void onSave();
            }}
            disabled={!canSave}
            loading={saving}
          >
            {saving ? 'Saving…' : s.slug === null ? 'Create' : 'Save changes'}
          </Button>
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
          Lowercase letters, numbers, dashes or underscores. 3–32 characters. Must start and end with a letter or number.
        </div>
        {saveError !== null ? (
          <div
            role="alert"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              padding: '8px 10px',
              marginTop: 10,
            }}
          >
            {saveError}
          </div>
        ) : null}
      </Card>

      {/* Attributed accounts */}
      <Card className="p-5 sm:p-6">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Attributed accounts
        </div>
        {s.attributed_accounts.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            No signups attributed yet. Share your link to get started.
          </div>
        ) : (
          <>
            <div data-testid="agency-referral-attributed-list">
              {s.attributed_accounts.map((a) => (
                <div
                  key={a.account_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr)',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: `1px solid ${C.borderSoft}`,
                    alignItems: 'center',
                  }}
                >
                  {/* UX follow-up 2026-10-10 — non-linked accounts show
                      a muted "Not a linked client" in the Name column
                      rather than repeating the domain (which already
                      lives in the second column). Linked clients
                      render the human-readable name the API returned. */}
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: a.is_linked_client ? C.text : C.muted,
                      fontStyle: a.is_linked_client ? 'normal' : 'italic',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    data-testid="agency-referral-attributed-name"
                    data-linked={a.is_linked_client ? 'true' : 'false'}
                  >
                    {a.is_linked_client
                      ? (a.name ?? a.primary_domain ?? 'Attributed account')
                      : 'Not a linked client'}
                  </div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {a.primary_domain ?? '—'}
                    {a.is_linked_client ? (
                      <span style={{ marginLeft: 8, fontSize: 10, color: C.muted }}>
                        · linked client
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                    {formatDate(a.created_at)}
                  </div>
                </div>
              ))}
            </div>
            {s.attributed_accounts_truncated ? (
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  marginTop: 10,
                  lineHeight: 1.5,
                }}
              >
                + {s.signups_total - s.attributed_accounts.length} more. The dashboard shows the most recent {s.attributed_accounts.length}.
              </div>
            ) : null}
          </>
        )}
      </Card>
    </ReferralShell>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function Counter({ label, value }: { label: string; value: string }): JSX.Element {
  const C = useC();
  return (
    <Card className="p-5">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ fontFamily: serifFont, fontSize: 36, color: C.text, lineHeight: 1 }}>
        {value}
      </div>
    </Card>
  );
}

function ReferralShell({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  const embedded = useIsEmbedded();
  if (embedded) {
    // AgencyPortal already renders the kicker, H1 and container.
    return <>{children}</>;
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>Agency</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Referral.
        </h1>
      </div>
      {children}
    </div>
  );
}
